import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Upload, Loader2, Check, AlertTriangle, Coins } from "lucide-react";

// Every job runs through the universal analyser · the prompt is what makes it cheap or expensive.
const JOBS = {
  document: [
    { id: "numbers", label: "Read it and pull the numbers", cost: "5 credits",
      prompt: "Read this document or screenshot and extract every measurable value with its unit and date. Return clean rows." },
    { id: "sessions", label: "Turn it into training sessions", cost: "5 credits",
      prompt: "Read this plan and list each training session: date, title, type (S&C / skill / physio / nutrition), duration in minutes and intensity." },
    { id: "matchsheet", label: "Read a match sheet into match stats", cost: "5 credits",
      prompt: "Read this match sheet and return one row per player-relevant stat: minutes, goals/points, key actions, and the result." },
  ],
  image: [
    { id: "numbers", label: "Read the report / screenshot", cost: "5 credits",
      prompt: "Read this image and extract every measurable value with its unit and date. If it is a body-composition or lab report, list each field." },
    { id: "posture", label: "Look at posture / position", cost: "5 credits",
      prompt: "Assess the athlete's position in this image: joint angles, alignment problems, and two concrete corrections." },
  ],
  audio: [
    { id: "notes", label: "Write down what was said and pull the numbers", cost: "10 credits",
      prompt: "Transcribe this recording, then list any numbers mentioned (weights, times, reps, pain scores) with what they refer to." },
  ],
  video: [
    { id: "technique", label: "Technique review with corrections", cost: "50 credits per 2 minutes",
      prompt: "Analyse the athlete's technique. For each phase list what is good, what is wrong, and the single most important correction." },
    { id: "trace_player", label: "Trace THIS athlete through the game → match stats", cost: "50 credits per 2 minutes · a full game is expensive",
      prompt: "Follow this one athlete through the passage of play. Report their involvements with timestamps (touches, passes, duels, shots, defensive actions), high-speed efforts, time on/off the ball, and finish with a match-stat summary table for this athlete only." },
    { id: "trace_team", label: "Trace the whole team → team match stats", cost: "50 credits per 2 minutes · the most expensive job",
      prompt: "Follow the whole team through this passage of play. Report shape and structure, transitions, set pieces, and a team match-stat summary (possession pattern, entries, shots, turnovers) with timestamps. Then list the two biggest coaching points." },
  ],
};

function kindOf(file) {
  const t = (file.type || "").toLowerCase();
  const n = (file.name || "").toLowerCase();
  if (t.startsWith("video/") || /\.(mp4|mov|m4v|avi|mkv|webm)$/.test(n)) return "video";
  if (t.startsWith("audio/") || /\.(mp3|wav|m4a|aac|ogg|flac)$/.test(n)) return "audio";
  if (t.startsWith("image/") || /\.(png|jpe?g|webp|heic|heif)$/.test(n)) return "image";
  return "document";
}

export default function BulkImport({ athleteIds, athletes, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [kind, setKind] = useState("document");
  const [job, setJob] = useState("numbers");
  const [busy, setBusy] = useState(false);
  const [confirmCost, setConfirmCost] = useState(null);
  const [rows, setRows] = useState([]);
  const inputRef = useRef(null);
  const picked = JOBS[kind] || JOBS.document;
  const chosen = picked.find((j) => j.id === job) || picked[0];
  const names = athletes.filter((a) => athleteIds.includes(a.athlete_id));

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const k = kindOf(f);
    setFile(f);
    setKind(k);
    setJob((JOBS[k] || JOBS.document)[0].id);
    setConfirmCost(null);
    setRows([]);
  };

  const run = async (confirm = false) => {
    if (!file) return toast.error("Pick a file first");
    setBusy(true);
    setRows([]);
    const out = [];
    for (const a of names) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("prompt", chosen.prompt);
      fd.append("assign_to", chosen.id);
      fd.append("confirm_cost", confirm ? "true" : "false");
      const queued = kind === "video" || kind === "audio" || kind === "document";  // long reads run in the background
      if (queued) fd.append("title", `${a.name} · ${chosen.label}`);
      try {
        const { data } = await api.post(
          `/athletes/${a.athlete_id}/media/${queued ? "analyze-async" : "analyze"}`, fd,
          { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
        out.push({ athlete: a.name, ok: true, cost: data.cost, queued,
                   kind: queued ? "queued · watch the jobs tray" : (data.analysis?.kind || kind) });
      } catch (e) {
        const d = e?.response?.data?.detail;
        if (d?.needs_confirmation) {
          setConfirmCost({ cost: d.estimated_cost, message: d.message });
          setBusy(false);
          return;
        }
        out.push({ athlete: a.name, ok: false, error: (typeof d === "string" ? d : d?.message) || "Failed" });
      }
      setRows([...out]);
    }
    setBusy(false);
    const good = out.filter((r) => r.ok).length;
    const anyQueued = out.some((r) => r.ok && r.queued);
    if (good) {
      toast.success(anyQueued ? `Queued for ${good} athlete(s)` : `Read for ${good} athlete(s)`,
        { description: anyQueued
            ? "It keeps running if you leave the page · progress is in the jobs tray, bottom right"
            : "Open the athlete's Data tab to review and save" });
      onDone?.();
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="bulk-import">
      <div className="bg-background border-2 border-foreground w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-border flex items-center gap-3 sticky top-0 bg-background">
          <Upload className="w-4 h-4" />
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-bold tracking-tight">Import data for {names.length} athlete(s)</h3>
            <p className="text-[11px] text-muted-foreground truncate">{names.map((a) => a.name).join(", ")}</p>
          </div>
          <button data-testid="bulk-import-close" onClick={onClose} aria-label="Close"
            className="text-muted-foreground hover:text-foreground transition-colors"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <p className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">1 · Give it any file</p>
            <div className="flex items-center gap-3 flex-wrap">
              <button data-testid="bulk-import-pick" onClick={() => inputRef.current?.click()}
                className="text-sm px-4 py-2 border border-foreground hover:bg-surface transition-colors">
                {file ? "Change file" : "Choose file"}
              </button>
              <span className="text-sm text-muted-foreground truncate max-w-[60%]" data-testid="bulk-import-file">
                {file ? `${file.name} · ${(file.size / 1048576).toFixed(1)} MB · read as ${kind}` : "video, image, PDF, sheet, audio — anything"}
              </span>
              <input ref={inputRef} type="file" className="hidden" onChange={onPick} data-testid="bulk-import-input" />
            </div>
          </div>

          <div>
            <p className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground mb-2">2 · What should it do with it?</p>
            <div className="space-y-2">
              {picked.map((j) => (
                <label key={j.id} data-testid={`bulk-job-${j.id}`}
                  className={`flex items-start gap-3 border p-3 cursor-pointer transition-colors ${job === j.id ? "border-foreground bg-surface" : "border-border hover:bg-surface"}`}>
                  <input type="radio" name="job" checked={job === j.id} onChange={() => { setJob(j.id); setConfirmCost(null); }} className="mt-1" />
                  <span className="min-w-0">
                    <span className="text-sm font-medium block">{j.label}</span>
                    <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Coins className="w-3 h-3" /> {j.cost}</span>
                  </span>
                </label>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">
              Longer video costs more because it is read two minutes at a time. Tracing a player or the whole team
              through a full game is the most expensive job on the platform — and it runs in the background, so you
              can close this and keep working.
            </p>
          </div>

          {confirmCost && (
            <div className="border-l-2 border-traffic-amber pl-3 py-1 text-sm" data-testid="bulk-import-confirm">
              <div className="flex items-center gap-1.5 text-traffic-amber text-[10px] uppercase tracking-widest font-bold">
                <AlertTriangle className="w-3 h-3" /> Check the cost
              </div>
              <p className="text-muted-foreground mt-1">{confirmCost.message} That is per athlete you selected.</p>
              <button data-testid="bulk-import-confirm-run" onClick={() => run(true)} disabled={busy}
                className="mt-2 text-sm px-4 py-2 bg-foreground text-background hover:bg-neutral-800 transition-colors disabled:opacity-60">
                Yes, use about {confirmCost.cost} credits each
              </button>
            </div>
          )}

          {rows.length > 0 && (
            <div className="border border-border" data-testid="bulk-import-results">
              {rows.map((r, i) => (
                <div key={i} className="px-3 py-2 text-sm flex items-center gap-2 border-b border-border last:border-0">
                  {r.ok ? <Check className="w-3.5 h-3.5 text-traffic-green" /> : <AlertTriangle className="w-3.5 h-3.5 text-traffic-red" />}
                  <span className="flex-1">{r.athlete}</span>
                  <span className="text-muted-foreground text-[11px]">{r.ok ? `read as ${r.kind} · ${r.cost} credits` : r.error}</span>
                </div>
              ))}
              <div className="px-3 py-2 text-[11px] text-muted-foreground">
                Everything it read is waiting in each athlete's Data tab · review it before you save.
              </div>
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-border flex items-center gap-2 sticky bottom-0 bg-background">
          <button data-testid="bulk-import-cancel" onClick={onClose} className="text-sm px-3 py-2 border border-border hover:bg-surface transition-colors">Close</button>
          <button data-testid="bulk-import-run" onClick={() => run(false)} disabled={busy || !file}
            className="ml-auto text-sm px-5 py-2 bg-foreground text-background hover:bg-neutral-800 transition-colors disabled:opacity-50 flex items-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}{busy ? "Reading…" : "Read it"}
          </button>
        </div>
      </div>
    </div>
  );
}
